import React, { useEffect, useRef, useState } from 'react';

const EuphoriaModal = ({ isOpen, onClose }) => {
    const mountRef = useRef(null);
    const fallbackCanvasRef = useRef(null);
    const [useFallback, setUseFallback] = useState(false);
    const [quote, setQuote] = useState('오늘도 성장하는 당신, 최고예요!');
    const [cta] = useState('지금 바로 한 고객에게 안부 보내기');

    useEffect(() => {
        if (!isOpen) return;
        let cleanup = () => { };
        (async () => {
            try {
                const THREE = await import('three');
                const mount = mountRef.current;
                const width = Math.min(window.innerWidth, 560);
                const height = Math.min(window.innerHeight * 0.6, 560);

                const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
                renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
                renderer.setSize(width, height);
                mount.innerHTML = '';
                mount.appendChild(renderer.domElement);

                const scene = new THREE.Scene();
                const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);
                camera.position.set(0, 0.4, 3.2);
                scene.add(new THREE.AmbientLight(0xffffff, 0.6));
                const dir = new THREE.DirectionalLight(0xffffff, 0.9); dir.position.set(2, 3, 4); scene.add(dir);

                // Preset builder helpers
                const choose = (arr) => arr[Math.floor(Math.random() * arr.length)];
                const theme = choose([
                    { base: 0xff55aa, emis: 0x551133, p: 0xffcc66 },
                    { base: 0x66e3ff, emis: 0x0a3a55, p: 0x88ffdd },
                    { base: 0xa3ff66, emis: 0x1f552a, p: 0xeaff66 },
                    { base: 0xff9cf0, emis: 0x5a1b59, p: 0xffd1f7 },
                ]);

                let mainObj = null; // animated object per preset
                const presetId = Math.floor(Math.random() * 4); // 0..3

                if (presetId === 0) {
                    // TorusKnot
                    const geo = new THREE.TorusKnotGeometry(0.6, 0.22, 180, 32);
                    const mat = new THREE.MeshStandardMaterial({ color: theme.base, emissive: theme.emis, metalness: 0.4, roughness: 0.25 });
                    mainObj = new THREE.Mesh(geo, mat);
                    scene.add(mainObj);
                } else if (presetId === 1) {
                    // Heart pulse
                    const heartShape = new THREE.Shape();
                    const x = 0, y = 0;
                    heartShape.moveTo(x + 0, y + 0.5);
                    heartShape.bezierCurveTo(x + 0, y + 0.2, x - 0.35, y + 0.2, x - 0.35, y + 0.5);
                    heartShape.bezierCurveTo(x - 0.35, y + 0.8, x, y + 1.0, x, y + 1.2);
                    heartShape.bezierCurveTo(x, y + 1.0, x + 0.35, y + 0.8, x + 0.35, y + 0.5);
                    heartShape.bezierCurveTo(x + 0.35, y + 0.2, x + 0, y + 0.2, x + 0, y + 0.5);
                    const geo = new THREE.ExtrudeGeometry(heartShape, { depth: 0.2, bevelEnabled: true, bevelSize: 0.02, bevelThickness: 0.04 });
                    geo.center();
                    const mat = new THREE.MeshStandardMaterial({ color: theme.base, emissive: theme.emis, metalness: 0.2, roughness: 0.3 });
                    mainObj = new THREE.Mesh(geo, mat);
                    mainObj.scale.set(0.9, 0.9, 0.9);
                    scene.add(mainObj);
                } else if (presetId === 2) {
                    // Star
                    const star = new THREE.Shape();
                    const R = 0.9, r = 0.38; const spikes = 5;
                    for (let i = 0; i < spikes * 2; i++) {
                        const rad = (i * Math.PI) / spikes;
                        const px = Math.cos(rad) * (i % 2 === 0 ? R : r);
                        const py = Math.sin(rad) * (i % 2 === 0 ? R : r);
                        if (i === 0) star.moveTo(px, py); else star.lineTo(px, py);
                    }
                    star.closePath();
                    const geo = new THREE.ExtrudeGeometry(star, { depth: 0.18, bevelEnabled: true, bevelSize: 0.02, bevelThickness: 0.04 });
                    geo.center();
                    const mat = new THREE.MeshStandardMaterial({ color: theme.base, emissive: theme.emis, metalness: 0.3, roughness: 0.25 });
                    mainObj = new THREE.Mesh(geo, mat); scene.add(mainObj);
                } else {
                    // Neon tube around a sinus curve
                    const path = new THREE.Curve();
                    path.getPoint = function (t) { const a = t * Math.PI * 2; return new THREE.Vector3(Math.cos(a) * 0.9, Math.sin(a * 2) * 0.25, Math.sin(a) * 0.9); };
                    const geo = new THREE.TubeGeometry(path, 220, 0.06, 16, true);
                    const mat = new THREE.MeshStandardMaterial({ color: theme.base, emissive: theme.base, emissiveIntensity: 0.6, metalness: 0.1, roughness: 0.2 });
                    mainObj = new THREE.Mesh(geo, mat); scene.add(mainObj);
                }

                const pCount = 700; const positions = new Float32Array(pCount * 3);
                for (let i = 0; i < pCount; i++) {
                    const r = 2.5 + Math.random() * 2.0; const a = Math.random() * Math.PI * 2; const y = (Math.random() - 0.5) * 1.6;
                    positions[i * 3] = Math.cos(a) * r; positions[i * 3 + 1] = y; positions[i * 3 + 2] = Math.sin(a) * r;
                }
                const pGeo = new THREE.BufferGeometry(); pGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
                const pMat = new THREE.PointsMaterial({ size: 0.025, color: theme.p, transparent: true, opacity: 0.9 });
                const points = new THREE.Points(pGeo, pMat); scene.add(points);

                let raf; const start = performance.now();
                const animate = (t) => {
                    const elapsed = (t - start) / 1000;
                    if (mainObj) {
                        mainObj.rotation.x = elapsed * 0.5; mainObj.rotation.y = elapsed * 0.8;
                        if (presetId === 1) {
                            const s = 1 + Math.sin(elapsed * 3) * 0.08; mainObj.scale.set(s, s, s);
                        }
                    }
                    const pos = pGeo.attributes.position;
                    for (let i = 0; i < pCount; i++) { const ix = i * 3; const s = 1 + Math.sin(elapsed + i * 0.02) * 0.003; pos.array[ix] *= s; pos.array[ix + 1] *= s; pos.array[ix + 2] *= s; }
                    pos.needsUpdate = true;
                    camera.position.x = Math.sin(elapsed * 0.6) * 0.6; camera.position.y = 0.4 + Math.sin(elapsed * 0.8) * 0.06; camera.lookAt(0, 0, 0);
                    renderer.render(scene, camera); raf = requestAnimationFrame(animate);
                };
                raf = requestAnimationFrame(animate);

                const onResize = () => { const w = Math.min(window.innerWidth, 560); const h = Math.min(window.innerHeight * 0.6, 560); renderer.setSize(w, h); camera.aspect = w / h; camera.updateProjectionMatrix(); };
                window.addEventListener('resize', onResize);
                cleanup = () => { cancelAnimationFrame(raf); window.removeEventListener('resize', onResize); if (mainObj?.geometry) mainObj.geometry.dispose(); pGeo.dispose(); renderer.dispose(); if (renderer.domElement?.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement); };
            } catch (e) {
                console.warn('[EuphoriaModal] three.js 로드 실패, 캔버스 폴백 사용', e);
                // 폴백: 간단한 컨페티 애니메이션
                setUseFallback(true);
                const cvs = fallbackCanvasRef.current;
                if (!cvs) return;
                const ctx = cvs.getContext('2d');
                const resize = () => {
                    cvs.width = Math.min(window.innerWidth, 560);
                    cvs.height = Math.min(window.innerHeight * 0.6, 560);
                };
                resize();
                window.addEventListener('resize', resize);
                const parts = Array.from({ length: 160 }, () => ({
                    x: Math.random() * cvs.width,
                    y: -20 - Math.random() * cvs.height,
                    r: 4 + Math.random() * 6,
                    vx: -1 + Math.random() * 2,
                    vy: 2 + Math.random() * 3,
                    c: `hsl(${Math.floor(Math.random() * 360)},90%,60%)`
                }));
                let raf;
                const loop = () => {
                    ctx.clearRect(0, 0, cvs.width, cvs.height);
                    parts.forEach(p => { p.x += p.vx; p.y += p.vy; if (p.y > cvs.height + 20) { p.y = -20; p.x = Math.random() * cvs.width; } ctx.fillStyle = p.c; ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2); ctx.fill(); });
                    raf = requestAnimationFrame(loop);
                };
                raf = requestAnimationFrame(loop);
                cleanup = () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
            }
        })();
        const msgPool = [
            '대단해요! 작은 실행이 큰 변화를 만듭니다 ✨',
            '지금의 당신, 최고의 타이밍입니다 🚀',
            '오늘의 도파민 충전 완료! 🔥',
            '한 걸음 더, 목표가 가까워지고 있어요 💎',
        ];
        setQuote(msgPool[Math.floor(Math.random() * msgPool.length)]);
        return () => cleanup();
    }, [isOpen]);

    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center">
            <div className="absolute inset-0 bg-black/70" onClick={onClose}></div>
            <div className="relative w-full max-w-[560px] mx-4 bg-white rounded-3xl shadow-2xl overflow-hidden">
                {useFallback ? (
                    <canvas ref={fallbackCanvasRef} className="w-full" />
                ) : (
                    <div ref={mountRef} className="w-full" />
                )}
                <div className="p-5 text-center">
                    <div className="text-2xl font-extrabold bg-gradient-to-r from-fuchsia-600 to-rose-600 bg-clip-text text-transparent mb-2">Euphoria Boost</div>
                    <p className="text-gray-800 font-semibold mb-3">{quote}</p>
                    <p className="text-sm text-gray-600 mb-4">작은 실행 미션:</p>
                    <button onClick={onClose} className="w-full py-3 rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white font-bold shadow hover:opacity-95">
                        {cta}
                    </button>
                    <div className="mt-3 text-xs text-gray-400">Device ID 보너스 이스터에그</div>
                </div>
            </div>
        </div>
    );
};

export default EuphoriaModal;


